import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { AuthContextType, AuthUser, UserRole } from '../types/auth.types'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * AuthProvider manages authentication state and provides auth actions.
 * Extracts firm_id and user_role from JWT custom claims.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [firmId, setFirmId] = useState<string | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<UserRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadRoleContextFromDb = useCallback(async (_userId: string) => {
    // Use SECURITY DEFINER RPCs — direct user_roles SELECT is restricted to partners only
    const [firmResult, roleResult] = await Promise.all([
      supabase.rpc('get_user_firm_id'),
      supabase.rpc('get_user_role'),
    ])

    if (firmResult.error) {
      console.error('Failed to load firm_id from DB:', firmResult.error.message)
    }
    if (roleResult.error) {
      console.error('Failed to load role from DB:', roleResult.error.message)
    }

    if (firmResult.data) setFirmId(firmResult.data)
    if (roleResult.data) setUserRole(roleResult.data as UserRole)
  }, [])

  /** Extract custom claims from JWT token */
  const extractClaims = useCallback(async (session: Session | null) => {
    if (!session?.access_token) {
      setFirmId(null)
      setClientId(null)
      setUserRole(null)
      return
    }

    try {
      // client_id has no DB fallback RPC and is lower-stakes (used for query
      // scoping, not routing decisions RLS wouldn't also enforce server-side)
      // so it's fine to read straight from the JWT.
      const payload = session.access_token.split('.')[1]
      const decoded = JSON.parse(atob(payload))
      setClientId(decoded.client_id || null)
    } catch {
      console.error('Failed to decode JWT claims')
      setClientId(null)
    }

    // firm_id / user_role drive role-gated routing (ProtectedRoute,
    // ClientProtectedRoute), so they're always resolved from the database
    // rather than trusted from the JWT's custom claims — those can go
    // stale (a user granted/changed access after their token was last
    // minted keeps whatever role was baked in until the token happens to
    // refresh), and this runs right at login, exactly when that staleness
    // is most likely to bite.
    if (session.user?.id) {
      await loadRoleContextFromDb(session.user.id)
    } else {
      setFirmId(null)
      setUserRole(null)
    }
  }, [loadRoleContextFromDb])

  /** Initialize auth state on mount */
  useEffect(() => {
    // The auth callback / password-reset pages call
    // supabase.auth.exchangeCodeForSession() themselves the instant they
    // mount. AuthProvider wraps the whole app, so its own getSession()
    // call below would fire at the exact same moment on those pages —
    // two concurrent Supabase auth calls racing for the SDK's internal
    // lock, which has been observed to throw "signal is aborted without
    // reason" instead of queuing safely, taking other in-flight calls
    // (like the firm_id/role RPCs) down with it. Skipping the redundant
    // call here removes that race; onAuthStateChange below still picks up
    // the resulting session once that page's own exchange completes.
    const skipInitialGetSession =
      window.location.pathname === '/auth/callback' ||
      window.location.pathname === '/auth/reset-password'

    const initAuth = async () => {
      try {
        if (skipInitialGetSession) return
        const { data: { session } } = await supabase.auth.getSession()
        setSession(session)
        setUser(session?.user as AuthUser || null)
        await extractClaims(session)
      } catch (error) {
        console.error('Auth initialization failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session)
        setUser(session?.user as AuthUser || null)
        await extractClaims(session)
        setIsLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [extractClaims])

  /** Sign in with email and password */
  const signIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  /** Create a new account (invitation-based sign-up) */
  const signUp = useCallback(async (email: string, password: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password,
      options: { emailRedirectTo: redirectTo ?? `${window.location.origin}/dashboard` },
    })
    if (error) throw error
  }, [])

  /** Sign in with magic link (passwordless) */
  const signInWithMagicLink = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    })
    if (error) throw error
  }, [])

  /** Send a password reset email */
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
    if (error) throw error
  }, [])

  /** Update the current user's password (used after a reset link, or when logged in) */
  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
  }, [])

  /** Sign out current user */
  const signOut = useCallback(async () => {
    // Clear local state first so the UI redirects immediately,
    // regardless of whether the Supabase server call succeeds.
    setUser(null)
    setSession(null)
    setFirmId(null)
    setClientId(null)
    setUserRole(null)
    await supabase.auth.signOut()
  }, [])

  /** Manually refresh session */
  const refreshSession = useCallback(async () => {
    const { data: { session }, error } = await supabase.auth.refreshSession()
    if (error) throw error
    setSession(session)
    await extractClaims(session)
  }, [extractClaims])

  const value: AuthContextType = {
    user,
    session,
    firmId,
    clientId,
    userRole,
    isLoading,
    isAuthenticated: !!user,
    isClient: userRole === 'client',
    signIn,
    signUp,
    signInWithMagicLink,
    resetPassword,
    updatePassword,
    signOut,
    refreshSession
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Hook to access auth context */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
